<template>
  <v-container>
    <h1 style="width:100%;text-align:center">{{title}}</h1>
    <div style="height:50px"></div>
    <v-row justify="center">
      <v-card color="rgb(108, 255, 178)" v-if="showThanksMsg" style="max-width:580px;">
        <v-card-text>
          <h4>Thank You For Uploading The Required Document(s)</h4>
        </v-card-text>
      </v-card>
      <v-card :color="cardColor" v-if="!showThanksMsg" style="max-width:580px;">
        <v-card-text>
          <h4>{{uploadInfo.instruction}}</h4>
          <div v-if="uploadInfo.details.length > 0" style="height:10px"></div>
          <ul v-for="detail in uploadInfo.details" :key="detail.documentName" v-if="hasDocumentToUpload">
            <li><em><strong>{{detail.documentName}}</strong></em><a target="_blank" rel="noopener noreferrer" :href="detail.downloadLink" v-if="detail.downloadLink"> - Download Copy</a></li>
          </ul>
        </v-card-text>
      </v-card>
      <div v-if="hasDocumentToUpload">
        <div style="height:40px"></div>
        <v-col v-for="detail in uploadInfo.details" :key="detail.documentName" style="max-width:580px" class="d-flex justify-center align-center">
          <v-row align="center">
            <v-col>
              <v-file-input
                v-model="files[detail.documentName]"
                :id="detail.documentName"
                :label="`${detail.buttonLabel} Here`"
                :accept="detail.acceptedMediaFileType.toString()"
                @click:clear="delete files[detail.documentName]"
                @change="fileChangeOperations(
                  detail.documentName,
                  files[detail.documentName],
                  detail.acceptedMediaFileType
                )"
              >
              </v-file-input>
            </v-col>
            <v-col cols="1" v-if="needToShowStatus(detail.documentName)">
              <v-progress-circular
                v-if="files[documentLoadingName(detail.documentName)]"
                indeterminate
                color="blue"
                class="ml-2 mb-5"
                length="20"
                width="2"
              ></v-progress-circular>
              <v-tooltip right v-if="uploadSuccessful(detail.documentName)">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon color="green"  class="mb-5"
                    v-bind="attrs"
                    v-on="on">
                    mdi-check
                  </v-icon>
                </template>
                <span>Document(s) successfully uploaded.</span>
              </v-tooltip>
              <v-tooltip right v-if="typeFailure(detail.documentName)">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon color="orange"  class="mb-5"
                  v-bind="attrs"
                  v-on="on">
                    mdi-alert-octagram
                  </v-icon>
                </template>
                <span>Invalid File Type</span>
              </v-tooltip>
              <v-tooltip right v-if="uploadFailed(detail.documentName)">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon color="red"  class="mb-5"
                    v-bind="attrs"
                    v-on="on">
                    mdi-alert-outline
                  </v-icon>
                </template>
                <span>Upload Failed</span>
              </v-tooltip>
            </v-col>
          </v-row>
          <div style="height:30px"></div>
        </v-col>
        <div style="height:40px"></div>
        <v-btn @click="upload" color="primary" large :disabled="submitBtnDisabled" class="d-flex justify-center align-center">
          <v-progress-circular
            v-if="isLoading"
            indeterminate
            color="white"
            class="ml-2"
            length="26"
            width="2"
          ></v-progress-circular>{{submitBtnTitle}}
        </v-btn>
      </div>
    </v-row>
  </div>
    <SnackbarNotifier
      :isShow="isShowSnackBar"
      :message="responseMessage"
      :snackType="snackbarType"
      :colour="snackBarColour"
      @eReset="resetSnackBar"
    />
    <StackingNotification
      v-if="notifications.length > 0"
      :stackNotifications="notifications"
      @eClose="removeNotification"
      @eClearAll="clearAllNotification"
    />
  </v-container>
</template>

<script>
import mixins from "vue-typed-mixins";
import SnackbarNotifier from "@/components/SnackbarNotifier.vue";
import SnackbarNotifierMixin from "@/mixins/SnackbarNotifierMixins";
import StackingNotification from "@/components/StackingNotification.vue";
import StackingNotificationMixins from "@/mixins/StackingNotificationMixins";
import axios from "axios";
import FormData from "form-data";
import { error } from "@/services/LoggerService";

const REMAIN_ON_PAGE_MESSAGE = 'Remain on the page until all uploads are complete';

export default mixins(SnackbarNotifierMixin, StackingNotificationMixins).extend({
  name: 'DocumentUploadArea',
  props: {
    title: {
      type: String,
      required: false,
      default: 'Document Submission'
    },
    uploadInfo: {
      type: Object,
      required: true
    }
  },
  components: {SnackbarNotifier, StackingNotification},
  data: () => ({
    isLoading: false,
    files: {},
    successfulUpload: [],
    failedUpload: [],
    typeCheckFailure: [],
  }),
  computed: {
    showThanksMsg(){
      const detailsLength = this.uploadInfo.details.length;
      return detailsLength > 0 && (detailsLength === this.successfulUpload.length)
      ? true: false;
    },
    totalUploadProcessed(){
      return this.successfulUpload.length + this.failedUpload.length;
    },
    submitBtnDisabled(){
      return (this.isLoading || !this.allDocumentUploaded || this.successfulUpload.length === this.uploadInfo.details?.length || this.typeCheckFailure.length > 0)
       ? true : false;
    },
    submitBtnTitle(){
      return this.isLoading ? '': 'Submit';
    },
    cardColor(){
      if(this.uploadInfo.instruction.includes('Inactive')) return 'rgb(255, 191, 0)'; //orange
      return this.uploadInfo.details?.length > 0 ? 'rgb(255, 253, 208)' : 'rgb(108, 255, 178)'; //yellow:green
    },
    hasDocumentToUpload(){
      return this.uploadInfo.details?.length > 0 ? true : false;
    },
    allDocumentUploaded(){
      let result = true;
      this.uploadInfo.details.map(detail => {
        if((this.files[detail.documentName] == null || this.files[detail.documentName] == undefined)
        && (!this.successfulUpload.includes(detail.documentName) || !this.failedUpload.includes(detail.documentName))) result = false;
      });
      return result;
    }
  },
  methods: {
    needToShowStatus(documentName){
      return (this.typeFailure(documentName) || this.uploadFailed(documentName) || this.uploadSuccessful(documentName) || this.files[this.documentLoadingName(documentName)])
        ? true : false;
    },
    documentFileEmpty(documentName){
      return (this.files[documentName] == null || this.files[documentName] == undefined) ? true : false;
    },
    documentLoadingName(documentName){
      return `${documentName}Loading`
    },
    startDocumentProcessing(documentName){
      this.disableFileInput(documentName);
      this.files[this.documentLoadingName(documentName)] = true;
    },
    stopDocumentProcessing(documentName){
      this.enableFileInput(documentName);
      this.files[this.documentLoadingName(documentName)] = false;
    },
    disableFileInput(documentName){
      document.getElementById(documentName).disabled = true;
      document.getElementById(documentName).readOnly = true;
    },
    enableFileInput(documentName){
      if(!this.successfulUpload.includes(documentName)){
        document.getElementById(documentName).disabled = false;
        document.getElementById(documentName).readOnly = false;
      }
    },
    uploadSuccessful(documentName){
      return this.successfulUpload.includes(documentName)
        ? true : false;
    },
    addDocumentToArray(inputArray,documentName){
      if(!inputArray.includes(documentName)) inputArray.push(documentName);
    },
    removeDocumentFromArray(inputArray,documentName){
      if(inputArray.includes(documentName)){
        var pos = inputArray.indexOf(documentName);
        if (pos > -1) {
          inputArray.splice(pos, 1);
        }
        return inputArray;
      }

    },
    fileChangeOperations(documentName, file, acceptedMediaFileType){
      this.checkFileType(documentName, file, acceptedMediaFileType)
      this.removeDocumentFromArray(this.failedUpload, documentName);
    },
    uploadFailed(documentName){
      return this.failedUpload.includes(documentName)
        ? true : false;
    },
    resetFailedUpload(){
      this.failedUpload = [];
    },
    typeFailure(documentName){
      return this.typeCheckFailure.includes(documentName)
        ? true : false;
    },
    checkFileType(documentName, file, acceptedMediaFileType){
      this.isFileTypeValid(file,acceptedMediaFileType)
        ? this.removeDocumentFromArray(this.typeCheckFailure, documentName)
        : this.addDocumentToArray(this.typeCheckFailure, documentName);
    },
    resetTypeFailure(){
      this.typeCheckFailure = [];
    },
    processingComplete(documentName){
      return (this.successfulUpload.includes(documentName) || this.failedUpload.includes(documentName))
      ? true : false;
    },
    handleResponse(){
      if(this.uploadInfo.details?.length === this.totalUploadProcessed){
        if(this.uploadInfo.details?.length === this.successfulUpload.length){
          this.addNotification({
            message: 'You can close the page now'
          });
          this.removeNotification(REMAIN_ON_PAGE_MESSAGE);
          this.showNotifier({
            message: `Document(s) successfully uploaded.`,
            snackbarType: "success",
          });
          this.isLoading = false;
        }
        if(this.failedUpload.length > 0){
          const failedAmount = this.failedUpload.length;
          this.showNotifier({
            message: `Failed To Upload ${failedAmount} Document${failedAmount > 1 ? 's':''}`,
            snackbarType: "error",
          });
          this.isLoading = false;
        }
      }
    },
    warnUser(){
      if(this.totalUploadProcessed == 0)this.addNotification({
        message: REMAIN_ON_PAGE_MESSAGE
      });
    },
    upload(){
      try{
        this.warnUser();
        this.resetTypeFailure();
        this.resetFailedUpload();
        this.isLoading = true;

        this.uploadInfo.details.map(async(detail) => {
          try{
            if(this.uploadSuccessful(detail.documentName)) return;

            this.startDocumentProcessing(detail.documentName);
            const formData = new FormData();
            const file = this.files[detail.documentName];
            const acceptedMediaFileType = detail.acceptedMediaFileType;
            const fileValid = this.isFileTypeValid(file, acceptedMediaFileType);

            if(!fileValid){
              this.addDocumentToArray(this.typeCheckFailure,detail.documentName);
              this.stopDocumentProcessing(detail.documentName);
              this.handleResponse();
              return;
            }

            Object.entries(detail.presignedInfo.fields).map(([field, value]) => {
              formData.append(field, value);
            });
            formData.append('content-type',file.type);
            formData.append('file', file);
            this.sendFile(formData,detail);
          }
          catch(err){
            error(err);
            this.addDocumentToArray(this.failedUpload,detail.documentName);
            this.stopDocumentProcessing(detail.documentName);
            this.handleResponse()
          }
        })
      }
      catch(err){
        this.isLoading = false;
        error(err);
        throw new Error("Failed Upload");
      }
    },
    sendFile(formData,detail){
      const vm = this;
      axios({
        method: "post",
        url: detail.presignedInfo.url,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      })
        .then(function (response) {
          if(response.status === 204){
            vm.addDocumentToArray(vm.successfulUpload,detail.documentName);
            vm.stopDocumentProcessing(detail.documentName);
            vm.handleResponse();
          }
        })
        .catch(function (response) {
          error(response);
          vm.addDocumentToArray(vm.failedUpload,detail.documentName);
          vm.stopDocumentProcessing(detail.documentName);
          vm.handleResponse();
        });
    },
    isFileTypeValid(file,acceptedMediaFileType){
      if(file.type == null || file.type == undefined || file.type === "") return false;
      return acceptedMediaFileType.includes(file.type);
    }
  }
})
</script>

<style scoped>
h4{
  margin: 10px;
}
</style>